import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ChangePassword = ({ forced = false }) => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.new_password !== form.confirm_password) {
      setError('New passwords do not match.');
      return;
    }
    if (form.new_password.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.changePassword({
        current_password: form.current_password,
        new_password: form.new_password,
      });
      updateUser(response.data);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f4f2ec',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Banner */}
      <Box sx={{ backgroundColor: '#00075e', py: 1.5, px: 4 }}>
        <Typography
          sx={{
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          Berkeley Technology &amp; Justice Lab
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', py: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LockResetIcon sx={{ fontSize: 48, color: '#2a52be', mb: 1 }} />
            <Typography
              variant="h4"
              sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 1 }}
            >
              {forced ? 'Set Your Password' : 'Change Password'}
            </Typography>
            {forced && (
              <Typography
                sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996' }}
              >
                You're signed in with a temporary password. Please set a new password to continue.
              </Typography>
            )}
          </Box>

          <Paper
            elevation={2}
            sx={{ p: 4, borderRadius: 2, backgroundColor: '#ffffff', border: '1px solid #e0ddd6' }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                required
                name="current_password"
                label="Current Password"
                type="password"
                value={form.current_password}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                margin="normal"
                required
                name="new_password"
                label="New Password"
                type="password"
                value={form.new_password}
                onChange={handleChange}
                helperText="At least 8 characters"
              />
              <TextField
                fullWidth
                margin="normal"
                required
                name="confirm_password"
                label="Confirm New Password"
                type="password"
                value={form.confirm_password}
                onChange={handleChange}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, py: 1.25 }}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Set New Password'}
              </Button>
              {!forced && (
                <Button
                  fullWidth
                  variant="text"
                  sx={{ mt: 1 }}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ChangePassword;
